import { useInformationDetail } from "@/api/services/main/information";
import { FileItem } from "@/components/elements/FileItem";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Notification } from "@/components/elements/Notification";
import { DATE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { dayjs } from "@/utils/dayjs";
import { sanitizeHTML } from "@/utils/pieces";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useValidateId } from "@/hooks/useValidateId";
import { AppRoutes } from "@/routes/config";

export const InformationDetailPage = () => {
  const queryResult = useInformationDetail();
  const { data: informationResult, isLoading } = queryResult;
  const informationData = informationResult?.data;
  const queryClient = useQueryClient();

  useValidateId("id", AppRoutes.information, queryResult);

  useEffect(() => {
    if (informationData) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.CHECK_UNREAD] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
    }
  }, [queryClient, informationData]);

  const renderContent = useMemo(() => {
    if (!informationData) return null;
    return (
      <Stack gap={1}>
        <Typography variant="sub16Semi">{informationData.title}</Typography>
        {informationData.content && <StyledNotificationDetail dangerouslySetInnerHTML={{ __html: sanitizeHTML(informationData.content) }} />}
        {informationData.filePath && <FileItem filePath={informationData.filePath} downloadable />}
      </Stack>
    );
  }, [informationData]);

  if (!informationData || isLoading) return <LoadingPage />;

  return (
    <Notification
      type={informationData.type}
      title={dayjs(informationData.startDate).format(DATE_FORMAT)}
    >
      {renderContent}
    </Notification>
  );
};

const StyledNotificationDetail = styled(Box)`
  p {
    min-height: 24px;
  }

  blockquote {
    border-left: 4px solid #ccc;
    margin: 8px 0;
    padding-left: 16px;
    color: #666;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }
`;
